import { IntegrationConfig } from "@types";
import clinicalTrialsLogo from "../assets/clinical-trials-logo.png";
import fdaLogo from "../assets/fda-logo.png";
import googleDriveLogo from "../assets/google-drive.svg";
import jira from "../assets/jira.png";
import pubmedLogo from "../assets/pubmed-integration-logo.png";

export const MCP_CONNECTION_PRESET_KEYS = [
  "atlassian_mcp",
  "google_drive_mcp",
  "granola_mcp",
  "pubmed_mcp",
  "clinical_trials_mcp",
  "fda_maude_mcp",
  "fda_recall_mcp",
] as const;

export type McpConnectionPresetKey =
  (typeof MCP_CONNECTION_PRESET_KEYS)[number];

export const ATLASSIAN_MCP_PRESET_KEY: McpConnectionPresetKey = "atlassian_mcp";
export const GOOGLE_DRIVE_MCP_PRESET_KEY: McpConnectionPresetKey =
  "google_drive_mcp";
export const GRANOLA_MCP_PRESET_KEY: McpConnectionPresetKey = "granola_mcp";
export const PUBMED_MCP_PRESET_KEY: McpConnectionPresetKey = "pubmed_mcp";
export const CLINICAL_TRIALS_MCP_PRESET_KEY: McpConnectionPresetKey =
  "clinical_trials_mcp";
export const FDA_MAUDE_MCP_PRESET_KEY: McpConnectionPresetKey = "fda_maude_mcp";
export const FDA_RECALL_MCP_PRESET_KEY: McpConnectionPresetKey =
  "fda_recall_mcp";

export interface McpConnectionPresetCardConfig
  extends Omit<IntegrationConfig, "id"> {
  id: string;
  documentationUrl?: string;
  documentationLabel?: string;
}

export interface McpConnectionPresetConfig {
  presetKey: McpConnectionPresetKey;
  serverUrl: string;
  authMode: "oauth" | "none";
  card: McpConnectionPresetCardConfig;
  failureMessage: string;
}

const getRuntimeEnvValue = (key: string): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window._env_?.[key];
};

export const ATLASSIAN_MCP_SERVER_URL = "https://mcp.atlassian.com/v1/mcp";
export const GOOGLE_DRIVE_MCP_SERVER_URL =
  "https://drivemcp.googleapis.com/mcp/v1";
export const GRANOLA_MCP_SERVER_URL = "https://mcp.granola.ai/mcp";
export const GRANOLA_MCP_DOCS_URL =
  "https://docs.granola.ai/help-center/sharing/integrations/mcp";
export const ATLASSIAN_MCP_DOCS_URL =
  "https://docs.formly.ai/user-settings/integrations-and-mcp-connections#atlassian-mcp-setup";
export const PUBMED_MCP_SERVER_URL = "https://pubmed.mcp.claude.com/mcp";
export const CLINICAL_TRIALS_MCP_SERVER_URL =
  "https://hcls.mcp.claude.com/clinical_trials/mcp";
export const FDA_MAUDE_MCP_SERVER_URL =
  getRuntimeEnvValue("FDA_MAUDE_MCP_SERVER_URL") ||
  "https://openfda-mcp.formly.ai/mcp";
export const FDA_RECALL_MCP_SERVER_URL =
  getRuntimeEnvValue("FDA_RECALL_MCP_SERVER_URL") ||
  "https://openfda-mcp.formly.ai/mcp";

export const MCP_PRESET_CONFIGS: McpConnectionPresetConfig[] = [
  {
    presetKey: ATLASSIAN_MCP_PRESET_KEY,
    serverUrl: ATLASSIAN_MCP_SERVER_URL,
    authMode: "oauth",
    card: {
      id: ATLASSIAN_MCP_PRESET_KEY,
      name: "Atlassian",
      logo: jira,
      description:
        "Connect Atlassian with one click for Jira, Confluence, and other Atlassian tools.",
      status: "disconnected",
      isComingSoon: false,
      isEnabled: true,
      authModel: "mcp_oauth",
      agentAccessMode: "agent_tools",
      documentationUrl: ATLASSIAN_MCP_DOCS_URL,
      documentationLabel: "Setup guide",
    },
    failureMessage:
      "Failed to start Atlassian OAuth flow. Open the Atlassian setup guide from this card and confirm your Formly domain is allowed in Atlassian Administration.",
  },
  {
    presetKey: GOOGLE_DRIVE_MCP_PRESET_KEY,
    serverUrl: GOOGLE_DRIVE_MCP_SERVER_URL,
    authMode: "oauth",
    card: {
      id: GOOGLE_DRIVE_MCP_PRESET_KEY,
      name: "Google Drive",
      logo: googleDriveLogo,
      description:
        "Connect Google Drive so the assistant can search, read, and create files you can access.",
      status: "disconnected",
      isComingSoon: false,
      isEnabled: true,
      authModel: "mcp_oauth",
      agentAccessMode: "agent_tools",
    },
    failureMessage:
      "Failed to connect Google Drive. Contact your Formly administrator if this integration is not configured.",
  },
  {
    presetKey: GRANOLA_MCP_PRESET_KEY,
    serverUrl: GRANOLA_MCP_SERVER_URL,
    authMode: "oauth",
    card: {
      id: GRANOLA_MCP_PRESET_KEY,
      name: "Granola",
      logo: "https://www.granola.ai/favicon/favicon-96x96.png",
      description:
        "Connect Granola so the assistant can search meeting notes, transcripts, decisions, and action items.",
      status: "disconnected",
      isComingSoon: false,
      isEnabled: true,
      authModel: "mcp_oauth",
      agentAccessMode: "agent_tools",
      documentationUrl: GRANOLA_MCP_DOCS_URL,
      documentationLabel: "Granola MCP guide",
    },
    failureMessage: "Failed to start Granola OAuth flow.",
  },
  {
    presetKey: PUBMED_MCP_PRESET_KEY,
    serverUrl: PUBMED_MCP_SERVER_URL,
    authMode: "none",
    card: {
      id: PUBMED_MCP_PRESET_KEY,
      name: "PubMed",
      logo: pubmedLogo,
      description:
        "Connect the hosted PubMed server for literature search and article lookup.",
      status: "disconnected",
      isComingSoon: false,
      isEnabled: true,
      authModel: "mcp_none",
      agentAccessMode: "agent_tools",
    },
    failureMessage: "Failed to connect PubMed.",
  },
  {
    presetKey: CLINICAL_TRIALS_MCP_PRESET_KEY,
    serverUrl: CLINICAL_TRIALS_MCP_SERVER_URL,
    authMode: "none",
    card: {
      id: CLINICAL_TRIALS_MCP_PRESET_KEY,
      name: "ClinicalTrials.gov",
      logo: clinicalTrialsLogo,
      description:
        "Connect the hosted ClinicalTrials.gov server for clinical study search and trial lookup.",
      status: "disconnected",
      isComingSoon: false,
      isEnabled: true,
      authModel: "mcp_none",
      agentAccessMode: "agent_tools",
    },
    failureMessage: "Failed to connect ClinicalTrials.gov.",
  },
  {
    presetKey: FDA_MAUDE_MCP_PRESET_KEY,
    serverUrl: FDA_MAUDE_MCP_SERVER_URL,
    authMode: "none",
    card: {
      id: FDA_MAUDE_MCP_PRESET_KEY,
      name: "FDA MAUDE",
      logo: fdaLogo,
      description:
        "Connect the Formly-hosted OpenFDA MAUDE server for medical device adverse event lookup.",
      status: "disconnected",
      isComingSoon: false,
      isEnabled: true,
      authModel: "mcp_none",
      agentAccessMode: "agent_tools",
    },
    failureMessage: "Failed to connect FDA MAUDE.",
  },
  {
    presetKey: FDA_RECALL_MCP_PRESET_KEY,
    serverUrl: FDA_RECALL_MCP_SERVER_URL,
    authMode: "none",
    card: {
      id: FDA_RECALL_MCP_PRESET_KEY,
      name: "FDA Recall Database",
      logo: fdaLogo,
      description:
        "Connect the Formly-hosted OpenFDA recall server for device recall and enforcement lookup.",
      status: "disconnected",
      isComingSoon: false,
      isEnabled: true,
      authModel: "mcp_none",
      agentAccessMode: "agent_tools",
    },
    failureMessage: "Failed to connect FDA Recall Database.",
  },
];

const normalizeMcpServerUrl = (serverUrl: string): string | null => {
  try {
    const url = new URL(serverUrl);
    url.hash = "";
    url.search = "";
    url.pathname = url.pathname.replace(/\/+$/, "");
    return url.toString();
  } catch {
    return null;
  }
};

export const getMcpPresetConfigByPresetKey = (
  presetKey: string | null | undefined
): McpConnectionPresetConfig | null => {
  if (!presetKey) {
    return null;
  }

  return (
    MCP_PRESET_CONFIGS.find((config) => config.presetKey === presetKey) ?? null
  );
};

export const getMcpPresetConfigByServerUrl = (
  serverUrl: string | null | undefined
): McpConnectionPresetConfig | null => {
  if (!serverUrl) {
    return null;
  }

  const normalizedServerUrl = normalizeMcpServerUrl(serverUrl);
  if (!normalizedServerUrl) {
    return null;
  }

  return (
    MCP_PRESET_CONFIGS.find(
      (config) =>
        normalizeMcpServerUrl(config.serverUrl) === normalizedServerUrl
    ) ?? null
  );
};

export const getMcpPresetConfigForConnection = (connection: {
  presetKey?: string | null;
  serverUrl?: string | null;
}): McpConnectionPresetConfig | null =>
  getMcpPresetConfigByPresetKey(connection.presetKey) ||
  getMcpPresetConfigByServerUrl(connection.serverUrl);
